import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';
import { errorMessages } from '@/lib/errorMessages';
import { logger } from '@/lib/logger';
import { IMAGE_VALIDATION } from '@/lib/constants';
import { isImageFile } from '@/lib/mediaFile';
import type { AdditionalTimeField } from '@/types/application';

export function useSceneController() {
  const {
    scenes,
    addScenes,
    updateScene,
    deleteScene,
    reorderScenes,
    applyTexts,
    resetAll,
  } = useAppStore();

  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const files = input.files;
      if (!files || files.length === 0) return;

      const imageFiles = Array.from(files)
        .filter(isImageFile)
        .sort((a, b) => a.name.localeCompare(b.name));

      // 同じ写真をもう一度選んでも change が発火するよう値を空に戻す
      input.value = '';

      if (imageFiles.length === 0) {
        const errMsg = errorMessages.noValidImages();
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      const newImages = imageFiles.map((file) => ({
        id: uuidv4(),
        imageUrl: URL.createObjectURL(file),
      }));

      addScenes(newImages);
    },
    [addScenes]
  );

  const handleImageDrop = useCallback(
    (files: FileList) => {
      if (!files || files.length === 0) return;

      const imageFiles = Array.from(files)
        .filter((file) => {
          if (!isImageFile(file)) {
            logger.warn(`サポートされていない画像形式: ${file.type}`, {
              fileName: file.name
            });
            return false;
          }

          if (file.size > IMAGE_VALIDATION.MAX_SIZE) {
            logger.warn(`画像が大きすぎます: ${(file.size / 1024 / 1024).toFixed(2)}MB`, {
              fileName: file.name,
              maxSize: `${IMAGE_VALIDATION.MAX_SIZE / 1024 / 1024}MB`,
            });
            const errMsg = errorMessages.imageTooLarge(file.name, file.size, IMAGE_VALIDATION.MAX_SIZE);
            toast.error(errMsg.title, { description: errMsg.message });
            return false;
          }

          return true;
        })
        .slice(0, IMAGE_VALIDATION.MAX_COUNT)
        .sort((a, b) => a.name.localeCompare(b.name));

      if (imageFiles.length === 0) {
        const errMsg = errorMessages.noValidImages();
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      const totalSize = imageFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > IMAGE_VALIDATION.MAX_TOTAL_SIZE) {
        const errMsg = errorMessages.totalImageSizeTooLarge(totalSize, IMAGE_VALIDATION.MAX_TOTAL_SIZE);
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      if (files.length > IMAGE_VALIDATION.MAX_COUNT) {
        logger.warn(`画像が最大枚数を超えました。最初の${IMAGE_VALIDATION.MAX_COUNT}枚のみ追加されます。`, {
          totalFiles: files.length,
          maxCount: IMAGE_VALIDATION.MAX_COUNT,
        });
      }

      logger.info('画像をドロップで追加', {
        count: imageFiles.length,
        totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`,
      });

      const newImages = imageFiles.map((file) => ({
        id: uuidv4(),
        imageUrl: URL.createObjectURL(file),
      }));

      addScenes(newImages);
    },
    [addScenes]
  );

  const handleApplyTexts = useCallback(
    (textInput: string) => {
      const lines = textInput
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (lines.length !== scenes.length) {
        const errMsg = errorMessages.textCountMismatch(lines.length, scenes.length);
        toast.error(errMsg.title, { description: errMsg.message });
        return false;
      }

      logger.info('テキストを適用', { textCount: lines.length, sceneCount: scenes.length });
      applyTexts(lines);
      return true;
    },
    [scenes.length, applyTexts]
  );

  const handleReorderScenes = useCallback(
    (sceneIds: string[]) => {
      reorderScenes(sceneIds);
    },
    [reorderScenes]
  );

  const handleDeleteScene = useCallback(
    (sceneId: string) => {
      deleteScene(sceneId);
    },
    [deleteScene]
  );

  const handleUpdateText = useCallback(
    (sceneId: string, text: string) => {
      updateScene(sceneId, { text });
    },
    [updateScene]
  );

  const handleUpdateAdditionalTime = useCallback(
    (sceneId: string, field: AdditionalTimeField, time: number) => {
      if (field === 'before') {
        updateScene(sceneId, { additionalDisplayTimeBefore: time });
      } else {
        updateScene(sceneId, { additionalDisplayTimeAfter: time });
      }
    },
    [updateScene]
  );

  const handleReset = useCallback(
    async (videoUrl: string | null) => {
      if (confirm('すべてのデータをリセットしますか？')) {
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
        }
        await resetAll();
        return true;
      }
      return false;
    },
    [resetAll]
  );

  return {
    handleImageUpload,
    handleImageDrop,
    handleApplyTexts,
    handleReorderScenes,
    handleDeleteScene,
    handleUpdateText,
    handleUpdateAdditionalTime,
    handleReset,
  };
}
